import {
  defineComponent,
  computed,
  onMounted,
  ref,
  watch,
  PropType,
} from "@vue/composition-api";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";
import AutocompleteId from "@/components/Autocomplete/AutocompleteId.vue";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";

import { trucksController } from "@/controllers/trucks";
import { buildFormLotUnitAutocomplete } from "@/models/LotUnit";

import {
  FormTruckLoad,
  prettifyTruckLoadStatus,
  buildFormTruckLoad,
} from "@/models/TruckLoad";

import { buildFormTruckAutocomplete } from "@/models/Truck";
import { JSConn } from "@/utilities/js-conn";

import { SellContract } from "@/models/SellContract";

import { format } from "date-fns";
import { ValueChanger } from "@/models/ValueChanger";

export default defineComponent({
  name: "buildTruckLoadForm",
  components: {
    FormErrors,
    AutocompleteId,
    LoadingAnimation,
  },
  props: {
    formTruckLoad: {
      type: Object as PropType<FormTruckLoad>,
      required: true,
    },
    tlDestination: {
      type: Object as PropType<SellContract>,
      required: false,
    },
    submitButtonText: {
      type: String,
      required: true,
    },
    submitting: {
      type: Boolean,
      required: true,
    },
  },

  setup(props, ctx) {
    const $store = ctx.root.$store;
    const dataLoaded = ref(false);

    let firstInput: any = ref(null);

    // Handle the date being changed
    // watch the scheduled date and adjust
    // the completed date if it hasn't been changed by user
    const changerCompletedDate = ref(ValueChanger.app);

    const changeCompletedDate = ({ target }: { target: HTMLInputElement }) => {
      changerCompletedDate.value = ValueChanger.user;
      props.formTruckLoad.completed_date = target.value;
    };

    watch(
      () => props.formTruckLoad.scheduled_date,
      (cDate: string, _prevDate: string) => {
        if (changerCompletedDate.value === ValueChanger.app) {
          props.formTruckLoad.completed_date = cDate;
        }
      },
    );

    let {
      truckLoadStatuses,
      loadTruckLoadStatusesIntoForm,
    } = buildFormTruckLoad();

    let { updateTruck } = buildFormTruckAutocomplete();

    const { updateLotUnit } = buildFormLotUnitAutocomplete();

    const localUpdateTruck = (evt: AutocompleteResult) => {
      updateTruck(evt, props.formTruckLoad.formTruck, props.formTruckLoad);
    };

    const localDestinationLabel = computed(() => {
      if (props.tlDestination) {
        return props.tlDestination.label;
      } else {
        return "Please pick a destination";
      }
    });

    onMounted(() => {
      loadTruckLoadStatusesIntoForm(truckLoadStatuses);

      const jsConnTrucks = new JSConn();
      const jsConnLotUnits = new JSConn();

      Promise.all([
        trucksController.needTrucks(jsConnTrucks),
        $store.dispatch("needLotUnits", jsConnLotUnits),
      ]).then(() => {
        // set the date if it is a new load
        if (props.formTruckLoad.id === "") {
          const td = format(new Date(), "yyyy-MM-dd");

          props.formTruckLoad.scheduled_date = td;
          props.formTruckLoad.completed_date = td;
        }

        dataLoaded.value = true;
        ctx.root.$nextTick(() => {
          if (firstInput.value) {
            firstInput.value.focus();
          }
        });
      });
    });

    const trucksForAutocomplete = computed(() => {
      return $store.getters.trucksForAutocompleteId;
    });

    const localUpdateWeightLotUnit = (evt: AutocompleteResult) => {
      updateLotUnit(
        evt,
        props.formTruckLoad.formWeightLotUnit,
        props.formTruckLoad,
        "weight_lot_unit_id",
      );
    };

    const weightLotUnitsForAutocomplete = computed(() => {
      return $store.getters.weightLotUnitsForAutocompleteId;
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      ctx.emit("sendFormSubmit", props.formTruckLoad);
    };

    return {
      dataLoaded,
      prettifyTruckLoadStatus,
      truckLoadStatuses,

      localUpdateTruck,
      trucksForAutocomplete,

      localDestinationLabel,
      localUpdateWeightLotUnit,
      weightLotUnitsForAutocomplete,

      onSubmit,

      changeCompletedDate,

      firstInput,
    };
  },
});
